import { resolveSchemaFieldType } from '../dsv-schema-utils';
import { generateLeaf } from './leaf-values';
import { resolveComposition } from './merge';

const MAX_DEPTH = 8;

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

function sameJson(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function generateObject(schema, depth) {
  const props = schema.properties || {};
  const out = {};
  Object.keys(props).forEach((key) => {
    const value = generateValue(props[key], depth + 1);
    if (value !== undefined) out[key] = value;
  });
  return out;
}

function generateArray(schema, depth) {
  const maxItems = typeof schema.maxItems === 'number' ? schema.maxItems : Infinity;
  if (maxItems === 0) return [];
  const minItems = typeof schema.minItems === 'number' ? schema.minItems : 0;
  const count = Math.min(Math.max(minItems, 1), maxItems);

  const items = [];
  for (let i = 0; i < count; i += 1) {
    // When schema.contains is present, make the first item satisfy it (P5 contingency).
    const itemSchema = (i === 0 && schema.contains && typeof schema.contains === 'object')
      ? schema.contains
      : Array.isArray(schema.prefixItems) && schema.prefixItems[i]
        ? schema.prefixItems[i]
        : (schema.items && typeof schema.items === 'object' ? schema.items : {});
    let item = generateValue(itemSchema, depth + 1);
    if (schema.uniqueItems && items.some((x) => sameJson(x, item))) {
      item = typeof item === 'string' ? `${item}-${i}` : item; // best-effort uniqueness
    }
    items.push(item);
  }
  return items;
}

/**
 * Recursively generate a constraint-valid value for any subschema.
 * Dispatches object/array/leaf, applying allOf/anyOf/oneOf composition first.
 * Depth-capped to guard against self-referential schemas.
 *
 * @param {object} schema - JSON Schema subschema
 * @param {number} [depth=0] - current recursion depth
 * @returns {*} generated value (null when the depth cap is hit)
 */
export function generateValue(schema, depth = 0) {
  if (!schema || typeof schema !== 'object') return 'string';
  if (depth > MAX_DEPTH) return null;

  if (Array.isArray(schema.examples) && schema.examples.length > 0) return schema.examples[0];
  if (hasOwn(schema, 'example')) return schema.example;
  if (hasOwn(schema, 'const')) return schema.const;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];

  const effective = resolveComposition(schema);
  const type = resolveSchemaFieldType(effective);

  if (type === 'object' || effective.properties) return generateObject(effective, depth);
  if (type === 'array' || effective.items || effective.prefixItems || effective.contains) return generateArray(effective, depth);

  // Infer numeric type from keywords when `type` is absent (e.g. inside an `if` subschema).
  const hasNumericKeyword = ['minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum', 'multipleOf']
    .some((k) => typeof effective[k] === 'number');
  if (hasNumericKeyword && !effective.type) {
    return generateLeaf({ ...effective, type: 'integer' });
  }

  return generateLeaf(effective);
}
